import {Injectable} from "@angular/core";
import {environment} from "../../../environments/environment";
import {Customization} from "../models/customization";

@Injectable({
  providedIn: 'root'
})
export class ApiRoutesService {
  readonly apiBaseUrl = environment.apiBase + '/api';

  members(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/members`;
  }

  hardware(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/hardwares`;
  }

  installation(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/installations`;
  }

  community(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/communities`;
  }

  customizations() {
    return `${this.apiBaseUrl}/customizations`;
  }

  customization(customizationId: string) {
    return `${this.apiBaseUrl}/customizations/${customizationId}`;
  }

  subscription(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/subscriptions`;
  }

  coupon(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/coupons`;
  }

  addon(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/addons`;
  }

  faq(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/faqs`;
  }

  games(isAdmin = false) {
    return `${this.apiBaseUrl}${isAdmin ? '/admin' : ''}/games`;
  }

  me() {
    return `${this.apiBaseUrl}/me`;
  }

  /** Changement de mot de passe self-service (profil), tous rôles confondus. */
  mePassword() {
    return `${this.me()}/password`;
  }

  // Membres de la plateforme (back-office admin) : rôles, réinitialisation de
  // mot de passe, création d'administrateurs.
  adminMembers() {
    return this.members(true);
  }

  adminMemberRoles(memberId: string) {
    return `${this.adminMembers()}/${memberId}/roles`;
  }

  adminMemberResetPassword(memberId: string) {
    return `${this.adminMembers()}/${memberId}/reset-password`;
  }

  adminAssignableRoles() {
    return `${this.adminMembers()}/roles`;
  }

  login() {
    return `${this.apiBaseUrl}/auth/login/organization`;
  }

  switchTenant() {
    return `${this.apiBaseUrl}/auth/switch`;
  }

  // Sessions simultanées (CDC §2) : enregistre la session du token courant.
  registerSession() {
    return `${this.apiBaseUrl}/sessions/register`;
  }

  // Déconnexion volontaire : libère la session du token courant.
  logoutSession() {
    return `${this.apiBaseUrl}/sessions/logout`;
  }

  register() {
    return `${this.apiBaseUrl}/register`;
  }

  organizations() {
    return `${this.apiBaseUrl}/organizations`;
  }

  organization(organizationId: string) {
    return `${this.organizations()}/${organizationId}`;
  }

  organizationCustomization(organizationId: string) {
    return `${this.organizations()}/${organizationId}/customizations`;
  }

  // CGVU compte self-service (CDC §9) : l'exploitant connecté met à jour les
  // CGVU de SON organisation (scope back via getConnectedOrganization).
  connectedOrganizationCgvu() {
    return `${this.organizations()}/me/cgvu`;
  }

  // Membres self-service (CDC §3) : l'exploitant connecté gère les membres de
  // SON organisation (scope back via getConnectedOrganization).
  connectedOrganizationMembers() {
    return `${this.organizations()}/me/members`;
  }

  connectedOrganizationMember(memberId: string) {
    return `${this.connectedOrganizationMembers()}/${memberId}`;
  }

  connectedOrganizationMemberResendActivation(memberId: string) {
    return `${this.connectedOrganizationMember(memberId)}/resend-activation`;
  }

  organizationMembers(organizationId: string) {
    return `${this.organization(organizationId)}/members`;
  }

  organizationMember(organizationId: string, memberId: string) {
    return `${this.organization(organizationId)}/members/${memberId}`;
  }

  languages() {
    return `${this.apiBaseUrl}/languages`;
  }

  languageCodes() {
    return `${this.languages()}/codes`;
  }

  translations() {
    return `${this.apiBaseUrl}/translations`;
  }

  publicTranslations(languageKey: string, totemId?: string, eventId?: string) {
    return `${this.apiBaseUrl}/public/translations?lang=${languageKey}`
      + (totemId ? `&totemId=${totemId}` : '')
      + (eventId ? `&eventId=${eventId}` : '');
  }

  language(key: string) {
    return `${this.languages()}/${key}`;
  }

  translation(translationId: string) {
    return `${this.translations()}/${translationId}`;
  }

  scenarios() {
    return `${this.apiBaseUrl}/scenarios`;
  }

  scenario(scenarioId: string) {
    return `${this.scenarios()}/${scenarioId}`;
  }

  scenarioProjectionImage(scenarioId: string) {
    return `${this.scenario(scenarioId)}/projection-image`;
  }

  // Fond du jeu — pendant du précédent pour l'autre image. Deux endpoints
  // parce que les deux images n'ont ni le même usage ni les mêmes gardes.
  scenarioDecorImage(scenarioId: string) {
    return `${this.scenario(scenarioId)}/decor-image`;
  }

  // Changement d'auteur d'un scénario (admin, CDC §11 incr 3).
  scenarioAuthor(scenarioId: string) {
    return `${this.scenario(scenarioId)}/author`;
  }

  wallToken(eventId: string) {
    return `${this.apiBaseUrl}/wall/events/${eventId}/token`;
  }

  // Endpoint public proxy. Le token signé est ajouté en query param `t`
  // par le composant Wall (pas par le service) car la route est ouverte
  // au-delà de la session JWT habituelle.
  wallImage(eventId: string) {
    return `${this.apiBaseUrl}/wall/events/${eventId}/image`;
  }

  themes() {
    return `${this.apiBaseUrl}/themes`;
  }

  theme(themeId: string) {
    return `${this.themes()}/${themeId}`;
  }

  themeArchive(themeId: string) {
    return `${this.theme(themeId)}/archive`;
  }

  themeUnarchive(themeId: string) {
    return `${this.theme(themeId)}/unarchive`;
  }

  questions(themeId: string) {
    return `${this.theme(themeId)}/questions`;
  }

  question(themeId: string, questionId: string) {
    return `${this.theme(themeId)}/questions/${questionId}`;
  }

  scenariosByOrganization(organizationId: string) {
    return `${this.organization(organizationId)}/scenarios`;
  }

  // WHERIZ Studio (CDC §11) — création + cycle de vie côté exploitant.
  scenariosStudio() {
    return `${this.scenarios()}/studio`;
  }

  // Lectures operator scopées à l'org connectée (WHERIZ Studio).
  scenarioMine(scenarioId: string) {
    return `${this.scenarios()}/mine/${scenarioId}`;
  }

  scenarioMineProjectionImage(scenarioId: string) {
    return `${this.scenarioMine(scenarioId)}/projection-image`;
  }

  scenarioMineDecorImage(scenarioId: string) {
    return `${this.scenarioMine(scenarioId)}/decor-image`;
  }

  /** Un événement en cours, toutes organisations, joue-t-il ce jeu ? (booléen) */
  scenarioMineLive(scenarioId: string) {
    return `${this.scenarioMine(scenarioId)}/live`;
  }

  scenariosActiveCount() {
    return `${this.scenarios()}/mine/active-count`;
  }

  scenarioActivate(scenarioId: string) {
    return `${this.scenario(scenarioId)}/activate`;
  }

  scenarioDeactivate(scenarioId: string) {
    return `${this.scenario(scenarioId)}/deactivate`;
  }

  scenarioArchive(scenarioId: string) {
    return `${this.scenario(scenarioId)}/archive`;
  }

  scenarioUnarchive(scenarioId: string) {
    return `${this.scenario(scenarioId)}/unarchive`;
  }

  // Historique des versions d'un scénario (CDC §11, module H).
  scenarioVersions(scenarioId: string) {
    return `${this.scenario(scenarioId)}/versions`;
  }

  scenarioVersionRestore(scenarioId: string, versionId: string) {
    return `${this.scenarioVersions(scenarioId)}/${versionId}/restore`;
  }

  // Même historique vu de l'admin : les routes ci-dessus sont exploitant,
  // scopées à l'organisation.
  adminScenarioVersions(scenarioId: string) {
    return `${this.apiBaseUrl}/admin/scenarios/${scenarioId}/versions`;
  }

  adminScenarioVersionRestore(scenarioId: string, versionId: string) {
    return `${this.adminScenarioVersions(scenarioId)}/${versionId}/restore`;
  }

  // Questions rattachées à un scénario Studio possédé (mode WALL).
  scenarioQuestions(scenarioId: string) {
    return `${this.scenario(scenarioId)}/questions`;
  }

  scenarioQuestion(scenarioId: string, questionId: string) {
    return `${this.scenarioQuestions(scenarioId)}/${questionId}`;
  }

  // Traductions d'une question par son auteur (Studio) — clés déduites côté serveur.
  scenarioQuestionTranslations(scenarioId: string, questionId: string) {
    return `${this.scenarioQuestion(scenarioId, questionId)}/translations`;
  }

  // Banque d'un jeu vue de l'admin : elle s'atteignait par le décor, ce qui
  // laissait les jeux Wall — qui portent leurs questions sur eux-mêmes — hors
  // de portée du back-office.
  adminGameQuestions(scenarioId: string) {
    return `${this.apiBaseUrl}/admin/scenarios/${scenarioId}/questions`;
  }

  adminGameQuestion(scenarioId: string, questionId: string) {
    return `${this.adminGameQuestions(scenarioId)}/${questionId}`;
  }

  // WHERIZ Studio (CDC §11) — thèmes propres à l'exploitant (mode CUBE).
  myThemes() {
    return `${this.themes()}/mine`;
  }

  myTheme(themeId: string) {
    return `${this.myThemes()}/${themeId}`;
  }

  myThemeQuestions(themeId: string) {
    return `${this.myThemes()}/${themeId}/questions`;
  }

  myThemeQuestion(themeId: string, questionId: string) {
    return `${this.myThemeQuestions(themeId)}/${questionId}`;
  }

  myThemeQuestionTranslations(themeId: string, questionId: string) {
    return `${this.myThemeQuestion(themeId, questionId)}/translations`;
  }

  totems() {
    return `${this.apiBaseUrl}/totems`;
  }

  totem(totemId: string) {
    return `${this.totems()}/${totemId}`;
  }

  totemCurrentEvent(totemId: string) {
    return `${this.totem(totemId)}/current-event`;
  }

  totemCurrentQuestions(totemId: string) {
    return `${this.totem(totemId)}/questions`;
  }

  totemsByOrganization(organizationId: string) {
    return `${this.organization(organizationId)}/totems`;
  }

  scenarioOrganization(organizationId: string, scenarioId: string) {
    return `${this.scenariosByOrganization(organizationId)}/${scenarioId}`;
  }

  // Droits per-scénario + restauration d'une attribution (admin, CDC §11 incr 3).
  scenarioOrganizationRights(organizationId: string, scenarioId: string) {
    return `${this.scenarioOrganization(organizationId, scenarioId)}/rights`;
  }

  /** Forçage admin déployé / non déployé d'une attribution (16/09). */
  scenarioOrganizationActive(organizationId: string, scenarioId: string) {
    return `${this.scenarioOrganization(organizationId, scenarioId)}/active`;
  }

  scenarioOrganizationUnarchive(organizationId: string, scenarioId: string) {
    return `${this.scenarioOrganization(organizationId, scenarioId)}/unarchive`;
  }

  events() {
    return `${this.apiBaseUrl}/events`;
  }

  event(eventId: string) {
    return `${this.events()}/${eventId}`;
  }

  eventArchive(eventId: string) {
    return `${this.event(eventId)}/archive`;
  }

  eventUnarchive(eventId: string) {
    return `${this.event(eventId)}/unarchive`;
  }

  eventStats(eventId: string) {
    return `${this.events()}/${eventId}/stats`;
  }

  // Accès joueur par eventId (scénarios sans totem / projection) — pendant
  // public de totemCurrentEvent / totemCurrentQuestions.
  eventGameEvent(eventId: string) {
    return `${this.event(eventId)}/game/event`;
  }

  // Écran des scores en direct : même événement, sans le contrôle de créneau du jeu.
  eventGameDisplay(eventId: string) {
    return `${this.event(eventId)}/game/display`;
  }

  eventGameQuestions(eventId: string) {
    return `${this.event(eventId)}/game/questions`;
  }

  // CGVU effectives d'un événement (CDC §9), endpoint public — consommé plus
  // tard par l'UI joueur (affichage + acceptation).
  eventCgvu(eventId: string) {
    return `${this.event(eventId)}/cgvu`;
  }

  eventGames(eventId: string) {
    return `${this.event(eventId)}/games`;
  }

  eventParticipants(eventId: string) {
    return `${this.event(eventId)}/participants`;
  }

  eventCustomization(eventId: string) {
    return `${this.events()}/${eventId}/customizations`;
  }

  game(gameId: string) {
    return `${this.apiBaseUrl}/games/${gameId}`;
  }

  gameDisplay(gameId: string) {
    return `${this.apiBaseUrl}/games/${gameId}/display`;
  }

  eventPlayers(eventId: string) {
    return `${this.event(eventId)}/players`;
  }

  publicFileUrl(id: string) {
    return `${this.apiBaseUrl}/files/${id}`;
  }

  // Le serveur n'envoie jamais d'`url` sur un fichier (le DTO ne porte que
  // l'id) : c'est ici, et uniquement ici, qu'on la pose. Tolère l'absence de
  // customization — le serveur la supprime dès qu'elle se vide.
  withPublicUrls(customization: Customization | null | undefined): void {
    for (const file of [customization?.backgroundImage, customization?.logo]) {
      if (file) {
        file.url = this.publicFileUrl(file.id);
      }
    }
  }

  organizationStats() {
    return `${this.apiBaseUrl}/stats/organizations`;
  }

  operatorStats() {
    return `${this.apiBaseUrl}/stats/operators`;
  }
}
